// Backend
import React from 'react';
import axios from '../../../helpers/AxiosInstance';

// Frontend
import { Download } from 'react-bootstrap-icons';
import { Grid, Cell } from 'react-foundation';

// Functionality
import { useParams, useLocation, Link } from 'react-router-dom';

// Components
import {
  Header,
  DluchApi,
  FlashMessage,
  ProgressBar,
  ErrorRender,
  Flashable,
  setToken,
} from '../../../constants/SharedComponents';

import useCurrentUser from '../../../hooks/useCurrentUser';

export function CanvasesConfirmationsNew() {
  // Params
  const { conversationId } = useParams();

  // API
  const currentUser = useCurrentUser();
  const { state } = useLocation();
  const token = setToken(currentUser);
  const headerParams = { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 };

  // Objects
  const [data, setData] = React.useState(null);
  const [canvasData, setCanvasData] = React.useState(null);
  const [submissionUnlocked, setSubmissionUnlocked] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  // Functions

  const onSave = React.useCallback(() => {
    setLoading(true);
    let Api = new DluchApi('ConversationDataEdit');

    let requestPayload = {
      conversationId: conversationId,
      canvasData: JSON.stringify(canvasData),
      isFinished: true,
    };

    axios
      .put(Api.ApiRequestUrl(), requestPayload, headerParams)
      .then(() => {
        setSubmissionUnlocked(false);
      })
      .catch((error) => {
        if (error.response?.data) {
          setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
        } else {
          setFlashMessageObject(Flashable(error.message, 'alert', false));
        }
      });

    setLoading(false);
  }, [canvasData]);

  const getData = async () => {
    setLoading(true);

    try {
      let Api = new DluchApi('ConversationsShow', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.conversation;
      setData(responseData);
      setError(false);
      getCategoryData();
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryData = async () => {
    setLoading(true);

    try {
      setError(false);
      getCanvasData();
    } catch {
      setError(false);
      getCanvasData();
    } finally {
      setLoading(false);
    }
  };

  const getCanvasData = async (category) => {
    setLoading(true);

    try {
      let Api = new DluchApi('ConversationDataShow', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.data;
      let canvasData = JSON.parse(responseData.canvasData);
      let locked = responseData.isFinished;

      if (locked) {
        setSubmissionUnlocked(false);
      } else {
        setSubmissionUnlocked(true);
      }

      setCanvasData(canvasData);
      setError(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  function retryFetch() {
    setLoading(true);
    setError(false);
    setData(null);
    getData();
  }

  // @see https://css-tricks.com/run-useeffect-only-once/#
  // The second param will ensure the useEffect only runs once.
  // eslint-disable-next-line
  React.useEffect(() => {
    if (currentUser?.token) {
      retryFetch();
    }

    if (state?.hasOwnProperty('flash')) {
      setFlashMessageObject(state.flash);
    }
  }, [currentUser, state]);

  return (
    <>
      <ProgressBar loading={loading} />
      <Header />
      <FlashMessage object={flashMessageObject} columnWrap={true} />
      <ErrorRender object={error} />

      {data && (
        <>
          <div className="page-content">
            <div className="page-content__header page-content__header--bordered page-content__header--fixed">
              <div className="grid-container">
                <Grid className="grid-padding-x align-middle">
                  <Cell small={12} large={2}>
                    <Link
                      to="/conversations"
                      className="page-content__button page-content__button--back"
                      id="page-navigation-button--go-back"
                    >
                      Back
                    </Link>
                  </Cell>

                  <Cell small={12} large={5} className="text-center" />

                  <Cell small={12} large={5} className="text-right">
                    <ul className="canvas-categories-navigation-list">
                      <li className="canvas-categories-navigation-list__item">
                        <Link
                          to={`/canvases/${data.id}/exports/new`}
                          id="page-navigation-button--download"
                          className="page-content__button--medium page-content__button--inline canvas-categories-navigation-list__save-button"
                        >
                          Download PDF{''}
                          <span className="canvas-categories-navigation-list__icon">
                            <Download size={14} />
                          </span>
                        </Link>
                      </li>
                    </ul>
                  </Cell>
                </Grid>
              </div>
            </div>
          </div>

          <div className="grid-container">
            <Grid className="grid-padding-x">
              <Cell small={12}>
                <div className="page-content page-content--margin">
                  {canvasData && submissionUnlocked && (
                    <>
                      <h1 className="page-content__title">Complete your conversation</h1>

                      <div className="page-content__styles">
                        <p>
                          This will lock the content and mean that it cannot be changed again.
                          Please ensure that you have completed the content for each Topic before
                          marking the conversation as complete. Marking this conversation as complete
                          signifies that both the Manager and Team Member agree to the actions and outcomes
                          created within the conversation.
                        </p>
                      </div>

                      <Link
                        to={`/canvases/${data.id}/categories/new`}
                        className="page-content__button page-content__button--inline"
                        id="page-navigation-button--return"
                      >
                        Back to conversation
                      </Link>

                      <button
                        onClick={onSave}
                        className="page-content__button page-content__button--inline"
                        id="page-form-button--submit"
                      >
                        Mark as complete
                      </button>
                    </>
                  )}

                  {canvasData && !submissionUnlocked && (
                    <p>Thank you, your conversation is now marked as complete.</p>
                  )}
                </div>
              </Cell>
            </Grid>
          </div>
        </>
      )}
    </>
  );
}
